import type { ReactNode } from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  composeRenderProps,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import type { CaptchaStatus } from "../useCaptcha";
import { focusButtonStyles } from "./primitive";

const STATUS_TEXT: Record<CaptchaStatus, string> = {
  idle: "",
  solving: "Are you a bot?",
  solved: "Nah \u2014 you\u2019re cool",
  failed: "You look suspicious\u2026",
};

const captchaButtonStyles = tv({
  extend: focusButtonStyles,
  base: [
    "relative inline-flex items-center justify-center gap-x-2 overflow-hidden font-medium",
    "cursor-pointer rounded-lg border border-transparent text-sm",
    "forced-colors:[--button-icon:ButtonText] forced-colors:hover:[--button-icon:ButtonText]",
    "*:data-[slot=icon]:my-1 *:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:text-(--button-icon)",
  ],
  variants: {
    intent: {
      primary: [
        "bg-primary text-primary-fg hover:bg-primary/90 pressed:bg-primary/80",
        "[--button-icon:var(--color-primary-fg)]",
      ],
      secondary: [
        "bg-secondary text-secondary-fg hover:bg-secondary/80 pressed:bg-secondary/70",
        "[--button-icon:var(--color-secondary-fg)]",
      ],
      danger: [
        "bg-danger text-danger-fg hover:bg-danger/90 pressed:bg-danger/80",
        "[--button-icon:var(--color-danger-fg)]",
      ],
      outline: [
        "border-border text-fg hover:bg-secondary pressed:bg-secondary/80",
        "[--button-icon:var(--color-fg)]",
      ],
      plain: [
        "text-fg hover:bg-secondary pressed:bg-secondary/80",
        "[--button-icon:var(--color-fg)]",
      ],
    },
    size: {
      "extra-small": "h-8 px-2.5 text-xs *:data-[slot=icon]:size-3.5",
      small: "h-9 px-3 text-sm",
      medium: "h-10 px-4 text-sm",
      large: "h-11 px-5 text-base",
    },
    shape: {
      square: "size-10 shrink-0",
      circle: "size-10 shrink-0 rounded-full",
    },
    isDisabled: {
      true: "cursor-default opacity-50",
    },
    isPending: {
      true: "cursor-default",
    },
  },
  defaultVariants: {
    intent: "primary",
    size: "medium",
  },
});

export interface CaptchaSubmitButtonProps extends Omit<AriaButtonProps, "children"> {
  intent?: "primary" | "secondary" | "danger" | "outline" | "plain";
  size?: "extra-small" | "small" | "medium" | "large";
  shape?: "square" | "circle";

  /** Current captcha lifecycle status from `useCaptcha()`. */
  captchaStatus: CaptchaStatus;

  /** Solve progress 0–100 from `useCaptcha()`. */
  captchaProgress: number;

  /** Button label shown when captcha is idle. */
  children?: ReactNode;
}

/**
 * A submit button that visualises proof-of-work CAPTCHA progress.
 *
 * While the captcha is solving, shows:
 * - A progress bar filling from left to right using the theme accent colour.
 * - Status text ("Are you a bot?" → "Nah — you're cool" / "You look suspicious…").
 * - A small spinner next to the text.
 *
 * Pass `captchaStatus` and `captchaProgress` from `useCaptcha()`.
 */
export function CaptchaSubmitButton({
  className,
  intent,
  size,
  shape,
  captchaStatus,
  captchaProgress,
  children,
  ...props
}: CaptchaSubmitButtonProps) {
  const isCaptchaActive = captchaStatus === "solving" || captchaStatus === "solved";
  const isPendingOrCaptcha = props.isPending || isCaptchaActive;

  return (
    <AriaButton
      className={composeRenderProps(className, (className, renderProps) =>
        captchaButtonStyles({
          ...renderProps,
          intent,
          size,
          shape,
          isPending: isPendingOrCaptcha,
          className,
        }),
      )}
      isDisabled={isPendingOrCaptcha || props.isDisabled}
      {...props}
    >
      {/* Progress bar */}
      {isCaptchaActive && (
        <div
          className="absolute inset-0 origin-left bg-primary-fg/15 transition-transform duration-200 ease-out"
          style={{ transform: `scaleX(${captchaProgress / 100})` }}
          aria-hidden="true"
        />
      )}

      {/* Content */}
      <span className="relative z-10 inline-flex items-center gap-2">
        {captchaStatus === "solving" && (
          <span
            className="inline-block size-4 animate-spin rounded-full border-2 border-primary-fg/30 border-t-primary-fg"
            aria-hidden="true"
          />
        )}
        {captchaStatus === "solved" && (
          <svg
            className="size-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
              clipRule="evenodd"
            />
          </svg>
        )}
        {captchaStatus === "failed" && (
          <svg
            className="size-4"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
        )}
        <span>
          {captchaStatus !== "idle" ? STATUS_TEXT[captchaStatus] : children}
        </span>
      </span>
    </AriaButton>
  );
}
